import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import apiClient from "../api/client";
import { ProfileViewModal } from "../components/profile/ProfileViewModal";
import { AuthContext } from "../App";


/**
 * ProfileRouteModalPage - Canonical profile viewer for /profile/:id route
 * 
 * Uses the same ProfileViewModal as Edit Profile Preview,
 * ensuring consistent profile rendering across all entry points.
 */
export default function ProfileRouteModalPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Determine if viewing self
  const isSelf = user?.id === id;

  useEffect(() => {
    let cancelled = false;

    async function loadProfile() {
      if (!id) {
        setError("Invalid profile ID");
        setLoading(false);
        return;
      }
      
      if (!token) {
        setError("Please log in to view profiles");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const res = await apiClient.get(`/users/profile/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setProfile(res.data);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          console.error("[ProfileRouteModalPage] Failed to load profile:", e);
          const status = e?.response?.status;
          const detail = e?.response?.data?.detail || "";
          
          if (status === 404) {
            if (detail.includes("not yet created")) {
              setError("This person hasn't set up their profile yet.");
            } else {
              setError("Profile not found.");
            }
          } else if (status === 403) {
            setError("You don't have permission to view this profile.");
          } else {
            setError("Couldn't load profile. Please try again.");
          }
          setLoading(false);
        }
      }
    }

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [id, token]);

  // Route-driven modal: close returns user to previous page
  const onClose = () => navigate(-1);

  // Loading state - show modal shell with loading indicator
  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 backdrop-blur-sm">
        <div className="text-center">
          <div className="animate-pulse text-white text-lg">Loading profile...</div>
        </div>
      </div>
    );
  }

  // Error state - show error with back option
  if (error) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 backdrop-blur-sm px-4">
        <div className="bg-slate-900 rounded-2xl border border-slate-700 p-8 max-w-md text-center">
          <div className="text-rose-400 text-lg font-semibold mb-2">Unable to load profile</div>
          <div className="text-slate-400 text-sm mb-6">{error}</div>
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-xl bg-slate-700 text-white text-sm font-medium hover:bg-slate-600 transition"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <ProfileViewModal
      open={true}
      onClose={onClose}
      profile={profile}
      isSelf={isSelf}
      source="route:/profile/:id"
    />
  );
}
